/* eslint-disable */
import React, { useMemo } from "react";
import { Card, Text, Button, Checkbox, Box, createStyles } from "@mantine/core";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useGetAttendanceYearSummary } from "api/attendance/getAttendanceYearSummary";

ChartJS.register(ArcElement, Tooltip, Legend);

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: 8,
    height: "100%",
    padding: 12,
    border: `1px solid ${theme.colors.gray[2]}`,
    boxShadow: theme.shadows.xs,
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    marginBottom: 8,
  },

  title: {
    fontWeight: 700,
    fontSize: 18,
  },

  content: {
    display: "flex",
    paddingTop: 2,
    alignItems: "center",
    width: "100%",

    [theme.fn.smallerThan("sm")]: {
      flexDirection: "column", // ✅ Responsive change only
      textAlign: "center",
    },
  },

  left: {},

  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 0",

    [theme.fn.smallerThan("sm")]: {
      justifyContent: "center",
    },
  },

  dot: {
    width: 12,
    height: 12,
    borderRadius: 12,
    display: "inline-block",
    marginRight: 8,
  },

  number: {
    fontWeight: 700,
    fontSize: 15,
  },

  label: {
    color: theme.colors.gray[5],
    marginLeft: 8,
  },

  chartWrap: {
    width: 300,
    height: 220,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  footer: {
    marginTop: 12,
  },
}));
const colorMap: Record<string, string> = {
  onTime: "#0c4b57",
  late: "#2ecc71",
  wfh: "#f36b2f",
  absent: "#e74c3c",
  sick: "#f1c40f",
};
export const useAttendanceMapper = (summaryData?: any) => {
  const items = useMemo(
    () => [
      {
        id: "on_time",
        label: "On Time",
        value: summaryData?.onTime ?? 0,
        color: colorMap.onTime,
      },
      {
        id: "late",
        label: "Late Attendance",
        value: summaryData?.late ?? 0,
        color: colorMap.late,
      },
      // {
      //   id: "wfh",
      //   label: "Work From Home",
      //   value: summaryData?.wfh ?? 0,
      //   color: colorMap.wfh,
      // },
      {
        id: "absent",
        label: "Absent",
        value: summaryData?.absent ?? 0,
        color: colorMap.absent,
      },
      {
        id: "sick",
        label: "Sick Leave",
        value: summaryData?.sick ?? 0,
        color: colorMap.sick,
      },
    ],
    [summaryData]
  );

  const chartDataRaw = useMemo(
    () =>
      items.map((i) => ({
        name: i.label,
        value: i.value,
        color: i.color,
      })),
    [items]
  );

  return { items, chartDataRaw };
};

export default function LeaveDetailsCard() {
  const { classes } = useStyles();
  const currentYear = new Date().getFullYear().toString();

  const { data, isLoading } = useGetAttendanceYearSummary(currentYear);
  const { items, chartDataRaw } = useAttendanceMapper(data?.data);
  const doughnutData = useMemo(
    () => ({
      labels: chartDataRaw.map((d) => d.name),
      datasets: [
        {
          data: chartDataRaw.map((d) => d.value),
          backgroundColor: chartDataRaw.map((d) => d.color),
          borderWidth: 0,
        },
      ],
    }),
    [chartDataRaw]
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      cutout: "66%",
      plugins: {
        legend: { display: false },
        tooltip: {
          padding: 8,
        },
      },
      rotation: -Math.PI / 2,
    }),
    []
  );

  return (
    <Card className={classes.card} radius="md">
      <div className={classes.header}>
        <Text className={classes.title}>Attendance Details</Text>
        <Button radius="sm" variant="light" size="xs">
          {currentYear}
        </Button>
      </div>

      <div className={classes.content}>
        <div className={classes.left}>
          {items.map((it) => (
            <div key={it.id} className={classes.item}>
              <span className={classes.dot} style={{ background: it.color }} />

              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text className={classes.number}>{it.value}</Text>
                  <Text className={classes.label}>{it.label}</Text>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={classes.chartWrap}>
          <div style={{ width: 220, height: 220 }}>
            <Doughnut data={doughnutData} options={options} />
          </div>
        </div>
      </div>
    </Card>
  );
}
